import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

const DEFAULT_FONT_SIZES = {
  kuranFontSize: 38,
  kuranMealFontSize: 14,
  duaArabFontSize: 35,
  duaMealFontSize: 14,
}

type FontSizes = typeof DEFAULT_FONT_SIZES

interface FontSizesContextType extends FontSizes {
  setFontSize: (_size: number, _fieldName: keyof FontSizes) => void
  resetToDefaults: () => void
}

const FontSizesContext = createContext<FontSizesContextType | null>(null)

// Provider component
interface FontSizesProviderProps {
  children: ReactNode
}

export function FontSizesProvider({ children }: FontSizesProviderProps) {
  // Initialize state with a function to avoid unnecessary calculations on every render
  const [fontSizes, setFontSizes] = useState<FontSizes>(() => {
    // Initialize with defaults
    return DEFAULT_FONT_SIZES
  })

  // Load from localStorage on first render
  useEffect(() => {
    try {
      const storedFontSizes = localStorage.getItem('fontSizes')
      if (storedFontSizes) {
        setFontSizes(JSON.parse(storedFontSizes))
      } else {
        setFontSizes(DEFAULT_FONT_SIZES)
      }
    } catch (error) {
      console.error('Error loading font sizes from localStorage:', error)
    }
  }, [])

  // Save to localStorage whenever font sizes change(not on first render)
  useEffect(() => {
    const storedFontSizes = localStorage.getItem('fontSizes')
    if (JSON.stringify(storedFontSizes) !== JSON.stringify(DEFAULT_FONT_SIZES)) {
      localStorage.setItem('fontSizes', JSON.stringify(fontSizes))
    } else {
      localStorage.removeItem('fontSizes')
    }
  }, [fontSizes])

  const setFontSize = (size: number, fieldName: keyof FontSizes) => {
    setFontSizes((prev) => ({ ...prev, [fieldName]: size }))
  }

  // Function to reset all font sizes to defaults
  const resetToDefaults = () => {
    setFontSizes(DEFAULT_FONT_SIZES)
  }

  // Create the context value
  const contextValue: FontSizesContextType = {
    ...fontSizes,
    setFontSize,
    resetToDefaults,
  }

  return (
    <FontSizesContext.Provider value={contextValue}>{children}</FontSizesContext.Provider>
  )
}

// Custom hook to use the font sizes context
const useFontSizes = (): FontSizesContextType => {
  const context = useContext(FontSizesContext)
  if (!context) {
    throw new Error('useFontSizes must be used within a FontSizesProvider')
  }
  return context
}

export default useFontSizes
